import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check } from 'lucide-react';
import apiClient from '../services/apiClient';
import {
  enqueue, loadOutbox, markConfirmed, markRefused, markTried, pendingItems,
  msAgo, isFinalError, storageWorks, toText, downloadText,
} from '../features/attendance/checkinOutbox';
import type { OutboxItem } from '../features/attendance/checkinOutbox';

// PUBLIC attendance — chemistryq.com/a. BP 23, 2026-09-21.
//
// Student-ID login is off pending the college's decision, so this page takes a
// check-in with no account: read the code off the projector, type your student
// ID and your name, done.
//
// It is used by 77 people at once, standing, on phones, in the first two
// minutes of a class. So: three fields, one button, big targets, and an answer
// that is unambiguous from two metres away.
//
// THE ONE RULE THIS SCREEN EXISTS TO ENFORCE: a press is never lost. The press
// is written to this phone before the network is touched, and it keeps being
// retried until the server takes it. A student who has pressed the button has
// done their part, whatever the wifi does next — so the page says "saved on
// this phone", not "error", and it never asks somebody who has already walked
// away to try again. See features/attendance/checkinOutbox.ts.

type Status = { open: boolean; session_label?: string | null; closes_at?: string };

// Nine digits, no prefix rule: real IDs start 100, 101, 102 and on, and every
// other entry point in the product accepts any nine. See the note in
// backend/src/services/AnonAttendanceService.js.
const ID_RE = /^\d{9}$/;
const ME_KEY = 'ace.checkin.me.v1';
const FLUSH_MS = 20000;

/** Remember who this phone belongs to, so next class is one field, not three.
 *  Fewer keystrokes is fewer typos, and a typo here is an unmatched row the
 *  instructor has to chase after class. */
function loadMe(): { student_id: string; name: string } {
  try {
    const raw = localStorage.getItem(ME_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return { student_id: String(p.student_id || ''), name: String(p.name || '') };
    }
  } catch { /* private window — just start blank */ }
  return { student_id: '', name: '' };
}

function saveMe(student_id: string, name: string): void {
  try {
    localStorage.setItem(ME_KEY, JSON.stringify({ student_id, name }));
  } catch { /* nothing to do; the form still works this session */ }
}

/**
 * When the register says it was recorded. The server sends its own clock back
 * (`at`), which is the only timestamp worth showing: a phone set to the wrong
 * timezone would otherwise print a confident, wrong time on what is meant to
 * be evidence. Falls back to the device only when the server did not say.
 */
function recordedAt(it: OutboxItem): string {
  if (!it.confirmedAt) return '';
  const t = new Date(it.confirmedAt);
  if (Number.isNaN(t.getTime())) return '';
  const time = t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return it.serverTimed ? `Recorded ${time}` : `Recorded ${time} (this phone's clock)`;
}

/**
 * A short buzz on success. The room is loud, the screen is small, and half of
 * them are already putting the phone away — a tactile confirmation lands when
 * a visual one is missed. Absent on desktop and on iOS Safari, and throws
 * behind some permission policies, so it is best-effort and never awaited.
 */
function buzz(): void {
  try { navigator.vibrate?.(120); } catch { /* not supported; the screen still says so */ }
}

function errorOf(err: unknown): { code?: string; message: string } {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    if (!err.response) return { message: 'No connection — still trying.' };
    return { code: data?.error, message: data?.message || 'Could not check you in.' };
  }
  return { message: 'Could not check you in.' };
}

export default function AnonAttendancePage() {
  const [status, setStatus] = useState<Status | null>(null);
  const me = useRef(loadMe());
  const [studentId, setStudentId] = useState(me.current.student_id);
  const [name, setName] = useState(me.current.name);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<OutboxItem[]>(() => loadOutbox());
  const [showAll, setShowAll] = useState(false);
  const idRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const canStore = useRef(storageWorks());

  // Poll the window state so the page stops saying "not open yet" on its own
  // once the instructor opens it — nobody should have to tell a room to refresh.
  const loadStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get<Status>('/api/v1/a/status');
      setStatus(data);
    } catch {
      setStatus({ open: false });
    }
  }, []);

  // ---- the outbox drain -------------------------------------------------
  // Runs on mount, whenever the phone comes back online, and on a timer. One
  // at a time and never concurrently, so a slow response cannot cause the same
  // item to be sent twice while the first is still in flight.
  const draining = useRef(false);
  const flush = useCallback(async () => {
    if (draining.current) return;
    const queue = pendingItems(loadOutbox());
    if (queue.length === 0) return;
    draining.current = true;
    try {
      for (const item of queue) {
        try {
          const { data } = await apiClient.post('/api/v1/a/checkin', {
            code: item.code,
            student_id: item.student_id,
            name: item.name,
            // An elapsed time, not a clock reading: the server judges the code
            // against the moment of the press, and a phone with a wrong clock
            // still knows how long ago that was.
            ms_ago: msAgo(item),
          });
          setItems(markConfirmed(item.id, data.ref, !!data.already, data.at, data.seq, data.total));
          buzz();
        } catch (err) {
          const { code: errCode, message } = errorOf(err);
          // Anything not explicitly final stays queued. Erring the other way
          // is how attendance gets lost.
          setItems(isFinalError(errCode)
            ? markRefused(item.id, message)
            : markTried(item.id, message));
        }
      }
    } finally {
      draining.current = false;
    }
  }, []);

  useEffect(() => {
    loadStatus();
    flush();
    const t = setInterval(() => { loadStatus(); flush(); }, FLUSH_MS);
    const onOnline = () => { loadStatus(); flush(); };
    window.addEventListener('online', onOnline);
    // Coming back to a backgrounded tab is the other moment worth retrying.
    const onVisible = () => { if (!document.hidden) onOnline(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadStatus, flush]);

  useEffect(() => {
    if (!studentId) idRef.current?.focus();
    else codeRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const idValid = ID_RE.test(studentId);
  const canSubmit = idValid && name.trim().length > 0 && code.trim().length >= 4 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    // On disk BEFORE the network. A request that dies in flight, a tab closed a
    // half-second later, a phone that runs out of battery — the press survives
    // all three, and the drain above finishes the job.
    const item = enqueue({
      student_id: studentId.trim(),
      name: name.trim(),
      code: code.trim(),
      session_label: status?.session_label ?? null,
    });
    setItems(loadOutbox());
    saveMe(studentId.trim(), name.trim());
    setCode('');
    try {
      const { data } = await apiClient.post('/api/v1/a/checkin', {
        code: item.code, student_id: item.student_id, name: item.name, ms_ago: msAgo(item),
      });
      setItems(markConfirmed(item.id, data.ref, !!data.already, data.at, data.seq, data.total));
      buzz();
    } catch (err) {
      const { code: errCode, message } = errorOf(err);
      setItems(isFinalError(errCode) ? markRefused(item.id, message) : markTried(item.id, message));
    } finally {
      setBusy(false);
    }
  };

  const field =
    'mt-1 w-full rounded-lg border-2 border-ws-border bg-ws-surface px-4 py-3 text-lg text-ws-ink '
    + 'focus:border-ws-accent focus:outline-none';

  // Echoed on the confirmation so a student can see the register got THEIR
  // entry, not the person's next to them. Read from the form, not the outbox —
  // a confirmed item deliberately no longer holds a name.
  const displayName = name.trim();
  const latest = items.length ? items[items.length - 1] : null;
  const stillSending = pendingItems(items).length;
  const shown = showAll ? [...items].reverse() : latest ? [latest] : [];

  return (
    <div className="min-h-screen bg-ws-bg px-4 py-8">
      <div className="mx-auto w-full max-w-md">
        <h1 className="text-2xl font-semibold text-ws-ink">Class check-in</h1>
        <p className="mt-1 text-sm text-ws-ink-2">ACE Core Chemistry · ACEH-3006</p>

        {/* State first, so nobody types into a form that cannot submit. */}
        {status && !status.open && (
          <div className="mt-5 rounded-lg border-2 border-ws-border bg-ws-surface-2 p-4">
            <p className="font-semibold text-ws-ink">Check-in is not open yet</p>
            <p className="mt-1 text-sm text-ws-ink-2">
              Wait for your instructor to put the code on the screen. This page updates on its own.
            </p>
          </div>
        )}
        {status?.open && (
          <div className="mt-5 rounded-lg border-2 border-ws-success bg-ws-success-quiet p-3">
            <p className="text-sm font-semibold text-ws-success">
              Check-in is open{status.session_label ? ` · ${status.session_label}` : ''}
            </p>
          </div>
        )}

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-ws-ink-2">Student ID</span>
            <input
              ref={idRef}
              value={studentId}
              onChange={(e) => setStudentId(e.target.value.replace(/\D/g, '').slice(0, 9))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="100123456"
              aria-describedby="id-hint"
              className={`${field} font-mono tracking-wide`}
            />
            <span id="id-hint" className="mt-1 block text-xs text-ws-ink-3">
              All 9 digits, as printed on your student card.
            </span>
            {studentId.length > 0 && !idValid && (
              <span className="mt-1 block text-xs font-medium text-ws-danger">
                {studentId.length !== 9
                  ? `That is ${studentId.length} digit${studentId.length === 1 ? '' : 's'} — it needs 9.`
                  : 'Enter all 9 digits.'}
              </span>
            )}
          </label>

          <label className="block">
            <span className="text-sm font-medium text-ws-ink-2">Your name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              placeholder="First and last name"
              className={field}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-ws-ink-2">Code on the screen</span>
            <input
              ref={codeRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="000000"
              onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(); }}
              className={`${field} text-center font-mono text-2xl tracking-[0.3em]`}
            />
            <span className="mt-1 block text-xs text-ws-ink-3">
              It changes every few seconds — type the one showing now.
            </span>
          </label>

          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="w-full rounded-lg bg-ws-accent px-4 py-3.5 text-base font-semibold text-ws-on-accent
                       transition hover:bg-ws-accent-hover disabled:opacity-40"
          >
            {busy ? 'Checking in…' : 'Check in'}
          </button>
        </div>

        {/* ---- what this phone is holding ---------------------------------
            The status of a press, stated from the student's point of view.
            "Saved on this phone" is the truth when the network is down, and it
            is the difference between a student who walks away confident and a
            student who walks away having lost the class. */}
        {shown.length > 0 && (
          <div className="mt-5 space-y-3">
            {shown.map((it) => (
              <div
                key={it.id}
                role="status"
                className={`rounded-lg border-2 p-4 ${
                  it.state === 'confirmed' ? 'border-ws-success bg-ws-success-quiet'
                    : it.state === 'pending' ? 'border-ws-warning bg-ws-warning/10'
                      : 'border-ws-danger bg-ws-danger/10'
                }`}
              >
                {it.state === 'confirmed' && (
                  <div className="text-center">
                    {/* Big, green, and readable from arm's length. A student
                        glancing down mid-conversation has to be able to tell
                        "done" from "not done" without reading a sentence. */}
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-ws-success">
                      <Check size={38} strokeWidth={3} className="text-white" />
                    </div>
                    <p className="mt-3 text-2xl font-bold text-ws-success">
                      {it.already ? 'Already checked in' : "You're checked in"}
                    </p>
                    {/* The number is the headline, not the reference. A student
                        can read "#23" out loud, compare it with the person next
                        to them, and the instructor can reconcile it against a
                        headcount. "3456-22" tells them nothing about whether
                        the class has them. */}
                    {typeof it.seq === 'number' && (
                      <div className="mt-3">
                        <p className="text-xs uppercase tracking-wide text-ws-ink-3">
                          You are number
                        </p>
                        <p className="font-mono text-5xl font-bold leading-none text-ws-ink">
                          {it.seq}
                        </p>
                        {typeof it.total === 'number' && (
                          <p className="mt-1 text-xs text-ws-ink-3">
                            of {it.total} checked in so far
                          </p>
                        )}
                      </div>
                    )}
                    {displayName && (
                      <p className="mt-1 text-lg font-semibold text-ws-ink">{displayName}</p>
                    )}
                    <p className="mt-1 text-sm text-ws-ink-2">
                      {it.session_label ? `${it.session_label} · ` : ''}
                      {recordedAt(it)}
                    </p>
                    {it.already && (
                      <p className="mt-1 text-xs text-ws-ink-3">
                        You were already on the register — this did not add a second entry.
                      </p>
                    )}
                    <div className="mt-3 border-t border-ws-success/40 pt-3">
                      <p className="text-xs uppercase tracking-wide text-ws-ink-3">Reference</p>
                      <p className="font-mono text-base font-semibold tracking-wide text-ws-ink-2">{it.ref}</p>
                      <p className="mt-1 text-xs text-ws-ink-3">
                        Show your number if your instructor asks. You can close this page.
                      </p>
                    </div>
                  </div>
                )}
                {it.state === 'pending' && (
                  <>
                    <p className="text-lg font-bold text-ws-ink">Saved on this phone</p>
                    <p className="mt-1 text-sm text-ws-ink-2">
                      Still sending to the college — this keeps trying on its own. You do not
                      need to press anything again. Leave this page open if you can.
                    </p>
                    {it.error && <p className="mt-1 text-xs text-ws-ink-3">{it.error}</p>}
                  </>
                )}
                {it.state === 'refused' && (
                  <>
                    <p className="font-semibold text-ws-danger">{it.error}</p>
                    <p className="mt-1 text-sm text-ws-ink-2">
                      Nothing was recorded for this attempt. Check the code on the screen and
                      your student ID, then press Check in again.
                    </p>
                  </>
                )}
                <p className="mt-2 text-xs text-ws-ink-4">
                  {new Date(it.at).toLocaleString()}
                  {it.session_label ? ` · ${it.session_label}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}

        {stillSending > 0 && (
          <p className="mt-3 text-center text-xs font-medium text-ws-warning">
            {stillSending} check-in{stillSending === 1 ? '' : 's'} on this phone not sent yet.
          </p>
        )}

        {!canStore.current && (
          <p className="mt-3 rounded-md border-l-[3px] border-ws-warning bg-ws-warning/10 px-3 py-2 text-xs text-ws-ink">
            This browser is not letting the page save anything (private mode, or site data is
            blocked). Check-in still works, but if it fails it cannot retry by itself — stay on
            this page until it says you are checked in.
          </p>
        )}

        {/* ---- extraction: works with the server completely down ---------- */}
        {items.length > 0 && (
          <div className="mt-6 border-t border-ws-border pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="text-sm font-medium text-ws-accent underline-offset-2 hover:underline"
              >
                {showAll ? 'Hide' : `Show all ${items.length} on this phone`}
              </button>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(toText(items)).catch(() => {}); }}
                  className="text-sm font-medium text-ws-ink-2 underline-offset-2 hover:underline"
                >
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => downloadText(items)}
                  className="text-sm font-medium text-ws-ink-2 underline-offset-2 hover:underline"
                >
                  Save a copy
                </button>
              </div>
            </div>
            <p className="mt-2 text-xs text-ws-ink-4">
              Your check-ins are kept on this phone as proof. Save or copy them if you ever need
              to show your instructor.
            </p>
          </div>
        )}

        <p className="mt-8 text-center text-xs text-ws-ink-3">
          No account needed. Your ID is stored in a shortened form, not in full.
        </p>
      </div>
    </div>
  );
}
